import { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { useBreadcrumb } from "../../contexts/BreadcrumbContext.jsx";
import { useUser } from "../../hooks/user.js";
import ClaimInfo from "../../components/ClaimInfo.jsx";
import TabList from "../../components/TabList.jsx";
import CommunicationsTable from "../../components/tables/CommunicationsTable.jsx";
import AttachmentsTable from "../../components/tables/AttachmentsTable.jsx";
import ClaimBookingsTable from "../../components/tables/ClaimBookingsTable.jsx";
import CommunicationModal from "../../components/modals/CommunicationModal.jsx";
import AttachmentModal from "../../components/modals/AttachmentModal.jsx";
import BookingModal from "../../components/modals/BookingModal.jsx";
import ClaimDataFormModal from "../../components/modals/ClaimDataFormModal.jsx";
import ClaimForm from "../../components/claim/ClaimForm.jsx";
import { useClaimBookingPermissions } from "../../hooks/permissions/useClaimBookingPermissions.jsx";
import { useClaimPermissions } from "../../hooks/permissions/useClaimPermissions.jsx";
import ClaimPreCheckModal from "../../components/modals/ClaimPreCheckModal.jsx";
import { useClaimActions } from "../../hooks/useClaimActions.jsx";
import ReminderModal from "../../components/modals/ReminderModal.jsx";
import ClaimNoteModal from "../../components/modals/ClaimNoteModal.jsx";
import { useClaimCommunicationPermissions } from "../../hooks/permissions/useClaimCommunicationPermissions.jsx";
import ForwardCommunicationModal from "../../components/modals/ForwardCommunicationModal.jsx";
import { activeBrand } from "../../theme/brands/index.js";

/**
 * Main Schaden claim/damage detail view
 */
export default function ClaimShowPage() {
  const { schaden_id, kommunikation_id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setBreadcrumbs } = useBreadcrumb();
  const { enqueueSnackbar } = useSnackbar();
  const { user, loading: userLoading } = useUser();
  const { findById, state: claimState } = useClaimActions();
  const { canViewAny: canViewAnyBookings } = useClaimBookingPermissions();
  const { canUpdateForm: canUpdateClaimForm } = useClaimPermissions();

  const [isClaimModalOpen, setIsClaimModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showContent, setShowContent] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const claim = claimState.data;
  const { canMessageInsurer } = useClaimCommunicationPermissions();

  const [activeTab, setActiveTab] = useState(
    () => searchParams.get("tab") || "kommunikation"
  );
  const [activeDialog, setActiveDialog] = useState(
    () => searchParams.get("dialog") || null
  );
  const [activeDialogId, setActiveDialogId] = useState(
    () => searchParams.get("dialog_id") || null
  );
  const [bookingMode, setBookingMode] = useState("normal"); // 'normal' or 'assignment'

  const model = useMemo(() => {
    if (claim?.formdata) {
      return {
        ...JSON.parse(claim.formdata),
        id: claim.id,
        costs: claim.costs || [],
      };
    }
    return {};
  }, [claim?.formdata, claim?.id]);

  const canEditForm = (currentClaim) => {
    if (!currentClaim) {
      return false;
    }

    const status_value = currentClaim.status?.value;
    // "awaiting_broker_completion" is the broker's completion phase — editable so
    // the broker can finish the claim (customer-owned fields stay frozen via the
    // PhaseRail read-only rendering + server enforcement).
    const is_editable_status = [
      "saved",
      "correction_customer",
      "awaiting_broker_completion",
    ].includes(status_value);

    return canUpdateClaimForm(currentClaim) && is_editable_status;
  };

  useEffect(() => {
    const dialogParam = searchParams.get("dialog");
    setActiveDialog(dialogParam || null);
    setActiveDialogId(searchParams.get("dialog_id") || null);
  }, [searchParams]);

  // Fetch claim data
  useEffect(() => {
    if (!schaden_id) {
      return;
    }

    findById(schaden_id, { setIsLoading });
  }, [schaden_id, enqueueSnackbar]);

  // Update breadcrumbs
  useEffect(() => {
    if (!claim) {
      setBreadcrumbs([
        { name: "Home", path: "/home" },
        { name: "Schäden", path: "/schaeden" },
        { name: "Laden...", path: `/schaden/${schaden_id}` },
      ]);
    } else {
      const claimNumber = claim?.schaden_nummer_makler || `SP-${schaden_id}`;
      setBreadcrumbs([
        { name: "Home", path: "/home" },
        { name: "Schäden", path: "/schaeden" },
        { name: claimNumber, path: `/schaden/${schaden_id}` },
      ]);
    }
  }, [schaden_id, claim?.schaden_nummer_makler, setBreadcrumbs]);

  const handleTabChange = (tabKey) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setActiveTab(tabKey);
    setSearchParams(
      (currentParams) => {
        const params = new URLSearchParams(currentParams);
        params.set("tab", tabKey);
        return params;
      },
      { preventScrollReset: true }
    );
  };

  const openDialog = (type, id = null) => {
    setActiveDialog(type);
    setActiveDialogId(id);

    setSearchParams(
      (currentParams) => {
        const params = new URLSearchParams(currentParams);
        params.set("dialog", type);
        if (id) {
          params.set("dialog_id", id);
        } else {
          params.delete("dialog_id");
        }
        return params;
      },
      { preventScrollReset: true }
    );
  };

  const closeDialog = useCallback(() => {
    setActiveDialog(null);
    setActiveDialogId(null);
    setSearchParams(
      (currentParams) => {
        const params = new URLSearchParams(currentParams);
        params.delete("dialog");
        params.delete("dialog_id");
        return params;
      },
      { preventScrollReset: true }
    );
  }, [setSearchParams]);

  // Define tabs configuration
  const tabs = useMemo(() => {
    const tabList = [
      {
        key: "kommunikation",
        label: "Kommunikation",
        count: claim?.kommunikation?.length,
        content: (
          <>
            <CommunicationsTable
              schaden_id={schaden_id}
              claim={claim}
              onRowClick={(row) => {
                if (row.type === "reminder") {
                  openDialog("reminder", row.id);
                } else if (row.type === "note") {
                  openDialog("note", row.id);
                } else {
                  openDialog("kommunikation", row.id);
                }
              }}
              onClickNewCommunication={() => {
                openDialog("kommunikation");
              }}
              onClickNewReminder={() => {
                openDialog("reminder");
              }}
              onClickNewNote={() => {
                openDialog("note");
              }}
              onClickForward={(e, communication) => {
                e.stopPropagation();
                openDialog("forward_communication", communication.id);
              }}
            />
          </>
        ),
      },
      {
        key: "anlagen",
        label: "Anlagen",
        count: claim?.anlagen?.length,
        content: (
          <AttachmentsTable
            schaden_id={schaden_id}
            onRowClick={(row) => {
              openDialog("anlage", row.id);
            }}
            onClickNew={() => {
              openDialog("anlage");
            }}
          />
        ),
      },
      {
        key: "schadenformular",
        label: "Schadenformular",
        content: (
          <ClaimForm
            model={model}
            sparte={claim?.sparte}
            hideTitle={true}
            disable={!canEditForm(claim)}
            claimStatus={claim?.status?.value}
          />
        ),
      },
    ];

    if (canViewAnyBookings()) {
      // Count assigned costs with remaining (unbooked) amount for the badge
      const assignedCostCount = (claim?.costs || []).filter(
        (cost) => cost.is_assigned && parseFloat(cost.remaining_amount || 0) > 0
      ).length;

      tabList.push({
        key: "buchungen",
        label: "Buchungen",
        count: claim?.booking_count,
        content: (
          <ClaimBookingsTable
            schaden_id={schaden_id}
            onRowClick={(row) => {
              openDialog("booking", row.id);
            }}
            onClickNew={() => {
              setBookingMode("normal");
              openDialog("booking");
            }}
            onClickNewAssignment={() => {
              setBookingMode("assignment");
              openDialog("booking");
            }}
            assignedCostCount={assignedCostCount}
          />
        ),
      });
    }

    return tabList;
  }, [claim, schaden_id, user, model, searchParams]);

  if (userLoading || !user) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          minHeight: "60vh",
        }}
      >
        <CircularProgress color="primary" size={48} />
        <Typography variant="body1" color="text.secondary">
          Lade Benutzerdaten...
        </Typography>
      </Box>
    );
  }

  if (isLoading || !claim) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          minHeight: "60vh",
        }}
      >
        <CircularProgress color="primary" size={48} />
        <Typography variant="body1" color="text.secondary">
          Schaden wird geladen...
        </Typography>
      </Box>
    );
  }

  const handleEdit = () => {
    setIsClaimModalOpen(true);
  };

  // Schmidt customer flow: while the claim is in a wizard-editing phase, the
  // Schadenformular is the standalone guided wizard (like the create page) — no
  // claim-detail card or tab bar above it, so its phase banner sits directly
  // under the header, matching the click dummy.
  const wizardFullBleed =
    activeBrand.features?.claimWizardFlow === "customer" &&
    activeTab === "schadenformular" &&
    ["saved", "correction_customer", "awaiting_broker_completion"].includes(
      claim?.status?.value
    );

  return (
    <Box>
      {wizardFullBleed ? (
        <Box
          sx={{
            mx: { xs: "-16px", md: "-32px", lg: "-40px" },
            mt: "-20px",
          }}
        >
          <ClaimForm
            model={model}
            sparte={claim?.sparte}
            hideTitle={true}
            disable={!canEditForm(claim)}
            claimStatus={claim?.status?.value}
          />
        </Box>
      ) : (
        <>
          {/* Sticky header container for ClaimInfo + TabList */}
          <Box
            sx={{
              position: "sticky",
              top: 104,
              zIndex: 3,
              borderTop: `3px solid background.paper`,
              backgroundColor: "background.paper",
              pb: 1,
              boxShadow: isScrolled
                ? "0 4px 8px -6px rgba(0, 0, 0, 0.25)"
                : "none",
              transition: "box-shadow 0.2s ease-in-out",
            }}
          >
            <ClaimInfo
              claim={claim}
              onEdit={handleEdit}
              showContent={showContent}
              setShowContent={setShowContent}
              onRefresh={() => findById(schaden_id, { setIsLoading })}
              readOnly={false}
              isInStickyContainer={true}
            />

            <TabList
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={handleTabChange}
              containerSx={{ mb: 0 }}
              renderContent={false}
            />
          </Box>

          {/* tab content - scrolls independently */}
          <Box sx={{ mt: 4 }}>
            {tabs.find((tab) => tab.key === activeTab)?.content}
          </Box>
        </>
      )}

      <CommunicationModal
        schaden_id={schaden_id}
        kommunikation_id={activeDialogId}
        open={activeDialog === "kommunikation"}
        canMessageInsurer={canMessageInsurer(claim)}
        onClose={closeDialog}
      />

      <ClaimNoteModal
        schaden_id={schaden_id}
        kommunikation_id={activeDialogId}
        open={activeDialog === "note"}
        onClose={closeDialog}
      />

      <ReminderModal
        schaden_id={schaden_id}
        kommunikation_id={activeDialogId}
        open={activeDialog === "reminder"}
        onClose={closeDialog}
      />

      <ForwardCommunicationModal
        schaden_id={schaden_id}
        kommunikation_id={activeDialogId}
        open={activeDialog === "forward_communication"}
        onClose={closeDialog}
      />

      <AttachmentModal
        schaden_id={schaden_id}
        open={activeDialog === "anlage"}
        onClose={closeDialog}
        attachment_id={activeDialogId}
      />

      <BookingModal
        schaden_id={schaden_id}
        open={activeDialog === "booking"}
        booking_id={activeDialogId}
        onClose={closeDialog}
        onSubmit={closeDialog}
        isEdit={!!activeDialogId}
        mode={bookingMode}
      />

      <ClaimDataFormModal
        claim_id={schaden_id}
        open={isClaimModalOpen}
        onClose={() => setIsClaimModalOpen(false)}
      />

      <ClaimPreCheckModal
        open={activeDialog === "pre_check"}
        onClose={closeDialog}
      />
    </Box>
  );
}
